import { useState } from 'react'

import {
  ActionIcon,
  Avatar,
  Box,
  Button as MantineButton,
  Menu,
  Popover,
  Tooltip,
} from '@mantine/core'

import { EmployeeStatus } from 'src/graphql/types/employees'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconClose from 'src/icons/IconClose'
import { buildFullName } from 'src/lib/formatters'

type Employee = {
  employeeId: string
  firstName?: string | null
  lastName?: string | null
  status?: string | null
}

type RecruiterOption = {
  label: string
  value: string
}

type EmployeesAssignmentSectionWithPopUpMenuProps = {
  applicant: {
    recruiter?: Employee | null
  }
  recruiters: RecruiterOption[]
  memberEmployees: Employee[]
  memberEmployeeIds: string[]
  memberEmployeeOptions: RecruiterOption[]
  onLeadEmployeeChange: (value: string) => void
  onMemberEmployeeChange: (nextMemberIds: string[]) => void
}

const EMPTY_VALUE = '-'

const formatEmployeeFullName = (employee?: Employee | null) => {
  if (!employee) {
    return EMPTY_VALUE
  }
  return buildFullName(employee) || EMPTY_VALUE
}

const EmployeesAssignmentSectionWithPopUpMenu = ({
  applicant,
  recruiters,
  memberEmployees,
  memberEmployeeIds,
  memberEmployeeOptions,
  onLeadEmployeeChange,
  onMemberEmployeeChange,
}: EmployeesAssignmentSectionWithPopUpMenuProps) => {
  const [isEmployeesDropdownOpen, setIsEmployeesDropdownOpen] = useState(false)

  return (
    <div className="flex w-full flex-col gap-2 text-[13px]">
      <div className="flex w-full items-center justify-between">
        <div className="text-xs text-doubleNickel-gray-500">
          Employees
        </div>
        <Popover
          opened={isEmployeesDropdownOpen}
          onChange={setIsEmployeesDropdownOpen}
          position="bottom-end"
          shadow="md"
          width={240}
        >
          <Popover.Target>
            <Box
              className="flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 hover:bg-doubleNickel-gray-50"
              onClick={() =>
                setIsEmployeesDropdownOpen(!isEmployeesDropdownOpen)
              }
            >
              <div className="flex items-center">
                {applicant?.recruiter && (
                  <Avatar.Group spacing="0">
                    <Tooltip label={formatEmployeeFullName(applicant.recruiter)}>
                      <Avatar
                        classNames={{
                          placeholder:
                            applicant.recruiter.status !== EmployeeStatus.ACTIVE
                              ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                              : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                        }}
                        size="sm"
                        radius="sm"
                      >
                        {applicant.recruiter.firstName?.[0] || ''}
                        {applicant.recruiter.lastName?.[0] || ''}
                      </Avatar>
                    </Tooltip>
                  </Avatar.Group>
                )}
                {memberEmployees.length > 0 && (
                  <>
                    <span className="mx-1.5 text-xs text-doubleNickel-gray-300">
                      |
                    </span>
                    <Avatar.Group spacing="0">
                      {(() => {
                        const firstMember = memberEmployees[0]
                        return (
                          <Tooltip
                            key={firstMember.employeeId}
                            label={formatEmployeeFullName(firstMember)}
                          >
                            <Avatar
                              classNames={{
                                placeholder:
                                  firstMember.status !== EmployeeStatus.ACTIVE
                                    ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                                    : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                              }}
                              size="sm"
                              radius="sm"
                            >
                              {firstMember.firstName?.[0] || ''}
                              {firstMember.lastName?.[0] || ''}
                            </Avatar>
                          </Tooltip>
                        )
                      })()}
                      {memberEmployees.length > 1 && (
                        <Tooltip
                          label={memberEmployees
                            .slice(1)
                            .map((member) => formatEmployeeFullName(member))
                            .join(', ')}
                        >
                          <Avatar
                            classNames={{
                              placeholder:
                                memberEmployees
                                  .slice(1)
                                  .every(
                                    (m) => m.status !== EmployeeStatus.ACTIVE
                                  )
                                  ? 'bg-doubleNickel-warning-100 text-doubleNickel-warning-600'
                                  : 'bg-doubleNickel-brand-25 text-doubleNickel-brand-500',
                            }}
                            size="sm"
                            radius="sm"
                          >
                            +{memberEmployees.length - 1}
                          </Avatar>
                        </Tooltip>
                      )}
                    </Avatar.Group>
                  </>
                )}
              </div>
              <ActionIcon variant="subtle" color="gray" size={16}>
                <IconChevronDown className="h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-700" />
              </ActionIcon>
            </Box>
          </Popover.Target>
          <Popover.Dropdown className="p-2">
            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full flex-col gap-1">
                <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                  Lead Employee
                </div>
                <Menu
                  shadow="md"
                  position="bottom-start"
                  width="target"
                  withinPortal={false}
                >
                  <Menu.Target>
                    <div className="flex cursor-pointer items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5 hover:border-doubleNickel-gray-300">
                      <span className="text-xs text-doubleNickel-gray-900">
                        {formatEmployeeFullName(applicant?.recruiter)}
                      </span>
                    </div>
                  </Menu.Target>
                  <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                    {recruiters.length === 0 ? (
                      <Menu.Item disabled>No employees available</Menu.Item>
                    ) : (
                      recruiters.map((employee) => (
                        <Menu.Item
                          key={employee.value}
                          onClick={() => onLeadEmployeeChange(employee.value)}
                        >
                          {employee.label}
                        </Menu.Item>
                      ))
                    )}
                  </Menu.Dropdown>
                </Menu>
              </div>
              <div className="flex w-full flex-col gap-1">
                <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                  Members ({memberEmployees.length})
                </div>
                <div className="flex w-full flex-col gap-1.5">
                  {memberEmployees.map((member) => (
                    <div
                      key={member.employeeId}
                      className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
                    >
                      <span className="text-xs text-doubleNickel-gray-900">
                        {formatEmployeeFullName(member)}
                      </span>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={20}
                        onClick={() =>
                          onMemberEmployeeChange(
                            memberEmployeeIds.filter(
                              (employeeId) => employeeId !== member.employeeId
                            )
                          )
                        }
                      >
                        <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </div>
                  ))}
                  <Menu
                    shadow="md"
                    position="bottom-start"
                    width="target"
                    withinPortal={false}
                  >
                    <Menu.Target>
                      <MantineButton
                        variant="subtle"
                        color="gray"
                        size="xs"
                        className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                        leftSection={
                          <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                        }
                      >
                        Add Member
                      </MantineButton>
                    </Menu.Target>
                    <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                      {memberEmployeeOptions.length === 0 ? (
                        <Menu.Item disabled>No employees available</Menu.Item>
                      ) : (
                        memberEmployeeOptions.map((employee) => (
                          <Menu.Item
                            key={employee.value}
                            onClick={() =>
                              onMemberEmployeeChange([
                                ...memberEmployeeIds,
                                employee.value,
                              ])
                            }
                          >
                            {employee.label}
                          </Menu.Item>
                        ))
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
            </div>
          </Popover.Dropdown>
        </Popover>
      </div>
    </div>
  )
}

export default EmployeesAssignmentSectionWithPopUpMenu
